import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSparkProjectData } from '../hooks/useSparkProjectData';
import { useIframeNavigation } from '../../../hooks/useIframeNavigation';
import styles from '../styles/ProjectView.module.css';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PublicIcon from '@mui/icons-material/Public';
import { ProjectSidebar } from '../components/ProjectSidebar';
import { SparkFlowStep, SparkCreativeType } from '../types/sparkTypes';
import { ProjectSetupPanel } from '../components/ProjectSetupPanel';
import { ProjectBriefingPanel } from '../components/ProjectBriefingPanel';
import { ProjectInsightsWallPanel, ProjectInsightsWallPanelHandle } from '../components/ProjectInsightsWallPanel';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
// Import the CreativeBoard component and its handle type
import CreativeBoard, { CreativeBoardHandle } from '../components/CreativeBoard';
// Import the CreativeEditor component
import CreativeEditor from '../components/modals/CreativeEditor';
// Import the BriefingAssistantOverlay component and handle type
import { BriefingAssistantOverlay, BriefingAssistantHandle } from '../components/briefing/BriefingAssistantOverlay';
// Import useAtlasStore for actions
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
// Import ConfirmationModal
import { ConfirmationModal } from '../components/modals/ConfirmationModal';
import { ResolvedSparkAction } from '../hooks/useSparkActions';
import { SparkActionModal } from '../components/modals/SparkActionModal';
// Import authenticatedFetch and helper
import { authenticatedFetch, parseJsonResponse } from '../../../api/apiClient';
// Import atlasClient for briefing export
import { atlasClient } from '../../../api/atlasClient';
// Import shared mode components
import { ModeAlertBar } from '../components/ModeAlertBar';
// Import GlobalJobPanel for header-inline agents trigger
import { GlobalJobPanel } from '../components/GlobalJobPanel';


export const ProjectView: React.FC = () => {
  const { projectFqn } = useParams<{ projectFqn: string }>();
  const location = useLocation();
  const navigate = useIframeNavigation();

  // Ensure projectFqn is treated as a string
  const safeProjectFqn = projectFqn || '';

  const { project, flow, creatives, jobs, isInitialLoading, isBackgroundRefreshing, error, refreshData, workerIsOnline } = useSparkProjectData(safeProjectFqn);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  // State for the creative editor
  const [selectedCreativeFqn, setSelectedCreativeFqn] = useState<string | null>(null);
  const [initialEditorTab, setInitialEditorTab] = useState<'details' | 'attachments' | 'deepsights' | null>(null);
  const [initialEditorIterationFqn, setInitialEditorIterationFqn] = useState<string | null>(null);
  const [initialEditorFeedbackId, setInitialEditorFeedbackId] = useState<string | null>(null);

  // Sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [creativesToDelete, setCreativesToDelete] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // State for Spark Action Modal
  const [isActionModalOpen, setIsActionModalOpen] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<ResolvedSparkAction | null>(null);
  const [currentActionArgs, setCurrentActionArgs] = useState<Record<string, any>>({});

  // Get store actions
  const deleteSparkCreatives = useAtlasStore((state: AtlasState) => state.deleteSparkCreatives);
  const setLastActiveSparkProject = useAtlasStore((state: AtlasState) => state.setLastActiveSparkProject);
  const closeSparkProject = useAtlasStore((state: AtlasState) => state.closeSparkProject);
  const reopenSparkProject = useAtlasStore((state: AtlasState) => state.reopenSparkProject);

  // Refs for child panels to access their action handlers
  const insightsPanelRef = useRef<ProjectInsightsWallPanelHandle>(null);
  const creativeBoardRef = useRef<CreativeBoardHandle>(null);
  const briefingAssistantRef = useRef<BriefingAssistantHandle>(null);

  // State for project Close/Reopen confirmation modals
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [isConfirmReopenOpen, setIsConfirmReopenOpen] = useState(false);
  const [isTogglingClosed, setIsTogglingClosed] = useState(false);

  // State lifted from CreativeBoard for reactivity
  const [isInsightsActionMenuOpen, setIsInsightsActionMenuOpen] = useState(false);
  const [creativeBoardState, setCreativeBoardState] = useState<{
    applicableActions: ResolvedSparkAction[];
    selectedCount: number;
    hasPublishedSelected: boolean;
  }>({ applicableActions: [], selectedCount: 0, hasPublishedSelected: false });

  // State lifted from InsightsPanel for reactivity
  const [insightsPanelLoading, setInsightsPanelLoading] = useState<{
    isSuggestingResources: boolean;
    isAddingResource: boolean;
    isRunningResearch: boolean;
    isUploading: boolean;
    isSuggestionMode: boolean;
    selectedSuggestionCount: number;
    // FSQ mode fields
    isFSQMode: boolean;
    isFSQGenerating: boolean;
    isFSQRunning: boolean;
    selectedFSQCount: number;
    totalFSQCount: number;
    fsqWebSearchEnabled: boolean;
  }>({ isSuggestingResources: false, isAddingResource: false, isRunningResearch: false, isUploading: false, isSuggestionMode: false, selectedSuggestionCount: 0, isFSQMode: false, isFSQGenerating: false, isFSQRunning: false, selectedFSQCount: 0, totalFSQCount: 0, fsqWebSearchEnabled: false });

  // State lifted from BriefingAssistantOverlay for alert bar
  const [briefingMode, setBriefingMode] = useState<{
    isEditing: boolean;
    isChatting: boolean;
    isStarting: boolean;
    isThinking: boolean;
  }>({ isEditing: false, isChatting: false, isStarting: false, isThinking: false });

  // Ref for dropdown container to detect outside clicks
  const insightsActionDropdownRef = useRef<HTMLDivElement>(null);

  // State for Briefing Assistant overlay
  const [isBriefingAssistantOpen, setIsBriefingAssistantOpen] = useState(false);

  // State for briefing DOCX download
  const [isDownloadingBriefing, setIsDownloadingBriefing] = useState(false);

  // State for briefing DOCX upload
  const [isUploadingDocx, setIsUploadingDocx] = useState(false);
  const [uploadedBriefingContent, setUploadedBriefingContent] = useState<string | null>(null);
  const docxFileInputRef = useRef<HTMLInputElement>(null);

  // State for navigation-away confirmation when assistant has changes
  const [pendingStepIndex, setPendingStepIndex] = useState<number | null>(null);
  const [isNavigateAwayModalOpen, setIsNavigateAwayModalOpen] = useState(false);

  // State for article availability (for suggest resources feature)
  const [hasArticlesAvailable, setHasArticlesAvailable] = useState<boolean>(false);

  // Fetch article availability on mount
  useEffect(() => {
    let cancelled = false;
    atlasClient.checkArticlesAvailable().then(available => {
      if (!cancelled) {
        setHasArticlesAvailable(available);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Close action menus when step changes
  useEffect(() => {
    setIsInsightsActionMenuOpen(false);
  }, [activeStepIndex]);

  // Close action menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (insightsActionDropdownRef.current && !insightsActionDropdownRef.current.contains(event.target as Node)) {
        setIsInsightsActionMenuOpen(false);
      }
    };
    if (isInsightsActionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isInsightsActionMenuOpen]);

  // Track data refresh trigger count to notify child components
  const [dataRefreshCounter, setDataRefreshCounter] = useState(0);
  const triggerRefreshData = useCallback(async () => {
    await refreshData();
    setDataRefreshCounter(c => c + 1);
  }, [refreshData]);

  // Track last-active project for global polling context
  useEffect(() => {
    if (safeProjectFqn) {
      setLastActiveSparkProject(safeProjectFqn);
    }
  }, [safeProjectFqn, setLastActiveSparkProject]);

  // Set initial step/creative from navigation state (e.g., returning from ProfileView or citation drill-down)
  useEffect(() => {
    const state: any = location.state;
    if (state && typeof state.initialStepIndex === 'number') {
      setActiveStepIndex(state.initialStepIndex);
    }
    if (state && state.initialCreativeFqn) {
      setSelectedCreativeFqn(state.initialCreativeFqn);
      setInitialEditorIterationFqn(state.initialIterationFqn || null);
      setInitialEditorTab(state.initialTab || null);
    }
    if (state && state.restoreBriefingAssistant) {
      setIsBriefingAssistantOpen(true);
      // Clear consumed flag from history to prevent re-triggering on remount
      const { restoreBriefingAssistant: _, ...remainingState } = state;
      window.history.replaceState(remainingState, '');
    }
  }, [location.state]);

  // Handler for step selection
  const handleStepSelect = (index: number) => {
    // If navigating away from briefing tab while assistant is open
    if (activeStepIndex === 1 && index !== 1 && isBriefingAssistantOpen) {
      // Check if there are unsaved changes
      if (briefingAssistantRef.current?.hasChanges) {
        // Store pending step and show confirmation
        setPendingStepIndex(index);
        setIsNavigateAwayModalOpen(true);
        return;
      }
      // No changes - just close assistant and navigate
      setIsBriefingAssistantOpen(false);
    }

    setActiveStepIndex(index);
    // Auto-collapse sidebar when in overlay mode (narrow screens)
    if (window.innerWidth < 1200 && !isSidebarCollapsed) {
      setIsSidebarCollapsed(true);
    }
  };

  // State for back navigation confirmation
  const [isBackNavigationPending, setIsBackNavigationPending] = useState(false);

  // Handle confirmation of navigate-away (discard changes)
  const handleConfirmNavigateAway = () => {
    setIsBriefingAssistantOpen(false);
    setIsNavigateAwayModalOpen(false);

    if (isBackNavigationPending) {
      setIsBackNavigationPending(false);
      navigate('/spark');
    } else if (pendingStepIndex !== null) {
      setActiveStepIndex(pendingStepIndex);
      setPendingStepIndex(null);
      // Auto-collapse sidebar when in overlay mode
      if (window.innerWidth < 1200 && !isSidebarCollapsed) {
        setIsSidebarCollapsed(true);
      }
    }
  };

  // Handle cancel of navigate-away (stay on briefing tab)
  const handleCancelNavigateAway = () => {
    setIsNavigateAwayModalOpen(false);
    setPendingStepIndex(null);
    setIsBackNavigationPending(false);
  };

  const handleBackClick = () => {
    // If briefing assistant is open with unsaved changes, confirm first
    if (isBriefingAssistantOpen && briefingAssistantRef.current?.hasChanges) {
      setIsBackNavigationPending(true);
      setIsNavigateAwayModalOpen(true);
      return;
    }
    navigate('/spark');
  };

  // Handler for downloading briefing as DOCX
  const handleDownloadBriefing = async () => {
    if (!project?.fqn || isDownloadingBriefing) return;
    setIsDownloadingBriefing(true);
    try {
      const blob = await atlasClient.downloadBriefingExport(project.fqn);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name || 'briefing'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading briefing:', error);
      toast.error('Failed to download briefing');
    } finally {
      setIsDownloadingBriefing(false);
    }
  };

  // Handler for uploading DOCX and converting to markdown
  const handleDocxFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !project?.fqn) return;

    // Reset input for re-selection of same file
    event.target.value = '';

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Please select a valid Word document (.docx)');
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIsUploadingDocx(true);
    const toastId = toast.loading(`Converting ${file.name}...`);

    try {
      const result = await atlasClient.uploadBriefingDocx(project.fqn, file);
      setUploadedBriefingContent(result.markdown);
      setIsBriefingAssistantOpen(true);
      toast.update(toastId, {
        render: 'Document converted successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 1500
      });
    } catch (error: any) {
      console.error('Failed to upload/convert DOCX:', error);
      const message = error?.response?.data?.error || error?.message || 'Failed to convert document';
      toast.update(toastId, {
        render: `Error: ${message}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setIsUploadingDocx(false);
    }
  };

  // Handlers for CreativeBoard actions
  const handleOpenCreativeEditor = (
    creativeFqn: string,
    iterationFqn?: string,
    initialTab?: 'details' | 'deepsights'
  ) => {
    setSelectedCreativeFqn(creativeFqn);
    setInitialEditorIterationFqn(iterationFqn || null);
    setInitialEditorFeedbackId(null);
    setInitialEditorTab(initialTab || null);
  };

  const handleCreativeClick = (creativeFqn: string) => {
    handleOpenCreativeEditor(creativeFqn);
  };

  const handleNavigateToBoardStep = useCallback((creativeTypeFqn: string) => {
    const stepIndex = flow?.steps?.findIndex((step) => step.creative_type_fqn === creativeTypeFqn) ?? -1;
    if (stepIndex === -1) {
      return;
    }

    setSelectedCreativeFqn(null);
    setInitialEditorIterationFqn(null);
    setInitialEditorFeedbackId(null);
    setInitialEditorTab(null);
    setActiveStepIndex(stepIndex + 3);
  }, [flow?.steps]);

  const handleQueueFeedbackScroll = useCallback((feedbackId: string) => {
    setInitialEditorFeedbackId(feedbackId);
  }, []);

  const resolveIterationForNavigation = useCallback(async (iterationFqn: string) => {
    try {
      return await atlasClient.getSparkIteration(iterationFqn);
    } catch {
      return null;
    }
  }, []);

  const resolveFeedbackIdForJob = useCallback(async (iterationFqn: string, jobFqn: string) => {
    try {
      const iteration = await atlasClient.getSparkIteration(iterationFqn);
      return iteration._feedback.find((feedback) => feedback._job_fqn === jobFqn)?._id || null;
    } catch {
      return null;
    }
  }, []);

  const handleCloseCreativeEditor = () => {
    // Clear the selected creative FQN
    setSelectedCreativeFqn(null);
    setInitialEditorIterationFqn(null);
    setInitialEditorFeedbackId(null);
    setInitialEditorTab(null);
    // Refresh data when closing editor in case changes were made
    triggerRefreshData();
  };

  // Updated to use modal instead of window.confirm
  const handleDeleteCreatives = async (fqns: string[]) => {
    if (!fqns.length) return;

    // Set state for deletion modal
    setCreativesToDelete(fqns);
    setIsDeleteModalOpen(true);
  };

  // Handle confirmation from modal
  const handleDeleteConfirm = async () => {
    if (!creativesToDelete.length) return;

    try {
      setIsDeleting(true);

      await deleteSparkCreatives(creativesToDelete);

      // Reset state
      setCreativesToDelete([]);

      // Refresh data after deletion
      await triggerRefreshData();
    } catch (error) {
      console.error('Failed to delete items:', error);
      toast.error('Failed to delete items. Please try again.');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  // --- Current Step Logic ---
  // Recalculate currentFlowStep using useMemo based on activeStepIndex and flow
  // With 3 setup steps (Project Setup, Project Briefing, Insights), flow steps start at index 3
  const currentFlowStep = useMemo(() => {
    if (!flow?.steps || activeStepIndex < 3) return null;
    return flow.steps[activeStepIndex - 3];
  }, [flow, activeStepIndex]);

  const handleCreateClick = async () => {
    if (!currentFlowStep || !projectFqn) return;
    const creativeType = currentFlowStep.creative_type_fqn;
    if (!creativeType || creativeType === '<none>') {
      toast.warn('Cannot create item in this step.');
      return;
    }
    try {
      // Use authenticatedFetch
      const response = await authenticatedFetch('/api/spark/creatives/create', {
        method: 'POST',
        // headers: { 'Content-Type': 'application/json' }, // Added by authenticatedFetch
        body: JSON.stringify({
          project_fqn: projectFqn,
          creative_type_fqn: creativeType
        }),
      });
      // Use parseJsonResponse
      await parseJsonResponse(response);
      await triggerRefreshData();
    } catch (error: any) {
      console.error('Failed to create creative:', error);
      toast.error(`Failed to create item: ${error.message || 'Unknown error'}`);
    }
  };

  // Updated handler to use active iterations and check input_type_fqn (Fix 1 & 4)
  const handleTriggerAction = (action: ResolvedSparkAction, creativeFqns: string[]) => {
    // Basic validation
    if ((action.action_type !== 'create' && !creativeFqns.length) || !projectFqn) {
      console.warn('Trigger Action condition not met', action, creativeFqns);
      return;
    }

    const baseActionArgs: Record<string, any> = {};

    // --- Handle input iterations (Fix 1 & 4) ---
    if (action.input_type_fqn && action.action_type !== 'create') {
      // Action requires specific input type, find active iterations
      const activeIterationFqns = creativeFqns
        .map(fqn => {
          const creative = creatives?.find(c => c.fqn === fqn);
          return creative?._active_iteration_fqn;
        })
        .filter((fqn): fqn is string => typeof fqn === 'string' && fqn !== null); // Filter out null/undefined

      // Check if we found active iterations for all selected creatives
      if (activeIterationFqns.length !== creativeFqns.length) {
        toast.error('Cannot trigger action: Not all selected items have an active iteration.');
        console.error('Missing active iterations for FQNs:', creativeFqns.filter(fqn => !activeIterationFqns.includes(creatives?.find(c => c.fqn === fqn)?._active_iteration_fqn || '')));
        return;
      }
      baseActionArgs.input_iteration_fqns = activeIterationFqns;
    }
    // If action.input_type_fqn is null/undefined, we don't add input_iteration_fqns
    // based on the clarification for Fix 4.

    // --- Handle output type ---
    if (action.output_type_fqn) {
      baseActionArgs.output_type_fqn = action.output_type_fqn;
    }
    // Add other potential pre-filled args based on action.input_schema if needed later

    // Set state for the modal
    setCurrentAction(action);
    setCurrentActionArgs(baseActionArgs);
    setIsActionModalOpen(true);
  };

  // Handler to close the action modal
  const handleCloseActionModal = () => {
    setIsActionModalOpen(false);
    setCurrentAction(null);
    setCurrentActionArgs({});
    triggerRefreshData();
  };

  // Handlers for Close/Reopen project
  const handleConfirmClose = async () => {
    if (!project) return;
    setIsTogglingClosed(true);
    try {
      await closeSparkProject(project.fqn);
      toast.success('Project closed');
      setIsConfirmCloseOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Operation failed';
      toast.error(message);
    } finally {
      setIsTogglingClosed(false);
    }
  };

  const handleConfirmReopen = async () => {
    if (!project) return;
    setIsTogglingClosed(true);
    try {
      await reopenSparkProject(project.fqn);
      toast.success('Project re-opened');
      setIsConfirmReopenOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Operation failed';
      toast.error(message);
    } finally {
      setIsTogglingClosed(false);
    }
  };

  // --- Combined Steps for Sidebar Navigation ---
  // Define the setup step objects - 3 setup steps before flow steps
  const projectSetupStep: SparkFlowStep = {
    name: 'Project',
    label: 'Project Setup',
    description: 'Configure project name, scope and team',
    creative_type_fqn: '<none>',
    media: [], invent: [], update: [], derive: []
  };
  const projectBriefingStep: SparkFlowStep = {
    name: 'Briefing',
    label: 'Project Briefing',
    description: 'Define the briefing for your project',
    creative_type_fqn: '<none>',
    media: [], invent: [], update: [], derive: []
  };
  const insightsBoardStep: SparkFlowStep = {
    name: 'Understanding',
    label: flow?.understanding_label || 'Understanding',
    description: flow?.understanding_description || 'Develop market and consumer understanding',
    creative_type_fqn: '<none>',
    media: [], invent: [], update: [], derive: []
  };

  // Combine setup steps with flow steps (3 setup steps + dynamic flow steps)
  const allSteps = [projectSetupStep, projectBriefingStep, insightsBoardStep, ...(flow?.steps || [])];

  // Compute completion status for each step
  const completedSteps = useMemo(() => {
    const completed: boolean[] = [];
    // Step 0: Project Setup - always completed
    completed.push(true);
    // Step 1: Briefing - completed if briefing is non-empty
    completed.push(!!project?.briefing && project.briefing.trim().length > 0);
    // Step 2: Resources - completed if at least one resource exists
    completed.push(!!(project?._resources && project._resources.length > 0));
    // Steps 3+: Innovation steps - completed if at least one creative exists for that step
    for (const step of (flow?.steps || [])) {
      const hasCreative = creatives?.some(c => c._creative_type_fqn === step.creative_type_fqn) ?? false;
      completed.push(hasCreative);
    }
    return completed;
  }, [project?.briefing, project?._resources, flow?.steps, creatives]);

  // Find the creative type for the selected creative
  const selectedCreative = useMemo(() => {
    if (!selectedCreativeFqn || !creatives) return null;
    return creatives.find(c => c.fqn === selectedCreativeFqn);
  }, [selectedCreativeFqn, creatives]);

  const selectedCreativeType = useMemo(() => {
    if (!selectedCreative || !flow?.creative_types) return undefined;
    return flow.creative_types.find((ct: SparkCreativeType) => ct.fqn === selectedCreative._creative_type_fqn);
  }, [selectedCreative, flow?.creative_types]);

  // Loading/Error checks
  if (isInitialLoading || (!project && !flow && !error)) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
        <p>Loading Project Data...</p>
      </div>
    );
  }

  if (error) {
    return <div className={styles.error}>{error || "An error occurred loading the Project."}</div>;
  }

  if (!project || !flow) {
    return <div className={styles.error}>Project or type data not found after load.</div>;
  }

  // When a creative is selected, render the editor as the full view
  if (selectedCreativeFqn && safeProjectFqn) {
    return (
      <CreativeEditor
        isOpen={true}
        onClose={handleCloseCreativeEditor}
        creativeFqn={selectedCreativeFqn}
        projectFqn={safeProjectFqn}
        projectTagPattern={project.tag_pattern}
        creativeType={selectedCreativeType}
        onDataRefreshed={triggerRefreshData}
        isProjectClosed={!!project.is_closed}
        initialTab={initialEditorTab || undefined}
        initialIterationFqn={initialEditorIterationFqn || undefined}
        initialFeedbackId={initialEditorFeedbackId || undefined}
        navigateToStep={handleNavigateToBoardStep}
        openCreativeEditor={handleOpenCreativeEditor}
        resolveIteration={resolveIterationForNavigation}
        resolveFeedbackIdForJob={resolveFeedbackIdForJob}
        scrollToFeedback={handleQueueFeedbackScroll}
      />
    );
  }

  // Get the current step for displaying in content header
  const currentStep = allSteps[activeStepIndex];

  return (
    <div className={styles.projectViewContainer}>
      {/* Display a warning if the DeepSights worker service is offline */}
      {!workerIsOnline && (
        <div className={styles.workerOfflineWarning}>
          <WarningAmberIcon className={styles.workerOfflineIcon} fontSize="small" />
          <span>DeepSights agents are offline</span>
        </div>
      )}

      {/* Conditionally render a subtle background refresh indicator */}
      {isBackgroundRefreshing && (
        <div className={styles.backgroundRefreshIndicator}>
          <CircularProgress size={20} />
          <span>Refreshing...</span>
        </div>
      )}

      {/* Unified panel containing header, sidebar, and content */}
      <div className={styles.projectPanel}>
        <div className={styles.projectHeader}>
          <div className={styles.headerRow}>
            <button className={styles.backButton} onClick={handleBackClick} title="Back to Projects">
              {/* SVG Back Arrow */}
              <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" width="24" height="24">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
              </svg>
            </button>
            <h1 className={styles.projectTitle}>{project.name || 'Spark Project'}</h1>
            <div className={styles.headerSpacer} />
            <GlobalJobPanel
              quietCreativeTypeFqn={activeStepIndex >= 3 ? flow.steps[activeStepIndex - 3]?.creative_type_fqn : null}
              navigateToStep={handleNavigateToBoardStep}
              openCreativeEditor={handleOpenCreativeEditor}
              resolveIteration={resolveIterationForNavigation}
              resolveFeedbackIdForJob={resolveFeedbackIdForJob}
              scrollToFeedback={handleQueueFeedbackScroll}
            />
          </div>
        </div>

        {/* Project body with sidebar and main content */}
        <div className={styles.projectBody}>
          {/* Sidebar navigation */}
          <ProjectSidebar
            steps={allSteps}
            activeIndex={activeStepIndex}
            completedSteps={completedSteps}
            onSelect={handleStepSelect}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
          />

          {/* Main content area */}
          <div className={styles.mainArea}>
            {/* Content header with selected tab name and action buttons */}
            <div className={styles.contentHeader}>
              <div className={styles.contentHeaderLeft}>
                <h2 className={styles.contentTitle}>{currentStep?.name || 'Content'}</h2>
                {currentStep?.description && (
                  <p className={styles.contentDescription}>{currentStep.description}</p>
                )}
              </div>
              <div className={styles.contentHeaderActions}>
                {/* Briefing actions - Edit with AI button, Upload, Download, or Save/Discard when editing */}
                {activeStepIndex === 1 && !isBriefingAssistantOpen && (
                  <>
                    <button
                      className={`${styles.headerActionButton} ${styles.headerActionButtonPrimary} ${styles.aiButton}`}
                      onClick={() => setIsBriefingAssistantOpen(true)}
                      disabled={!!project?.is_closed}
                      title={project?.is_closed ? 'Project is closed' : 'Edit briefing with AI assistant'}
                    >
                      <AutoAwesomeIcon fontSize="small" />
                      Edit briefing
                    </button>
                    <button
                      className={`${styles.headerActionButton} ${styles.headerActionButtonSecondary}`}
                      onClick={() => docxFileInputRef.current?.click()}
                      disabled={!!project?.is_closed || isUploadingDocx}
                      title={project?.is_closed ? 'Project is closed' : 'Upload a Word document'}
                    >
                      <UploadIcon fontSize="small" />
                      {isUploadingDocx ? 'Uploading...' : 'Upload'}
                    </button>
                    <button
                      className={`${styles.headerActionButton} ${styles.headerActionButtonSecondary}`}
                      onClick={handleDownloadBriefing}
                      disabled={!project?.briefing || isDownloadingBriefing}
                      title={!project?.briefing ? 'No briefing to download' : 'Download briefing as Word document'}
                    >
                      <DownloadIcon fontSize="small" />
                      {isDownloadingBriefing ? 'Downloading...' : 'Download'}
                    </button>
                  </>
                )}
                {activeStepIndex === 1 && isBriefingAssistantOpen && (
                  <>
                    <button
                      className={`${styles.headerActionButton} ${styles.headerActionButtonDanger}`}
                      onClick={() => briefingAssistantRef.current?.discardChanges()}
                      disabled={briefingAssistantRef.current?.isSaving}
                      title="Discard all changes"
                    >
                      Discard All Changes
                    </button>
                    <button
                      className={`${styles.headerActionButton} ${styles.headerActionButtonPrimary}`}
                      onClick={() => briefingAssistantRef.current?.saveAndClose()}
                      disabled={briefingAssistantRef.current?.isSaving}
                      title="Save changes and close editor"
                    >
                      {briefingAssistantRef.current?.isSaving ? 'Saving...' : 'Save & Close'}
                    </button>
                  </>
                )}

                {/* Project Setup actions - Close/Re-open button */}
                {activeStepIndex === 0 && project?.current_user_is_admin && (
                  <button
                    className={`${styles.headerActionButton} ${!project.is_closed ? styles.headerActionButtonDanger : ''}`}
                    onClick={() => { project.is_closed ? setIsConfirmReopenOpen(true) : setIsConfirmCloseOpen(true); }}
                    title={project.is_closed ? 'Re-open project' : 'Close project'}
                  >
                    {project.is_closed ? 'Re-open Project' : 'Close Project'}
                  </button>
                )}

                {/* Insights panel actions */}
                {activeStepIndex === 2 && !insightsPanelLoading.isSuggestionMode && !insightsPanelLoading.isFSQMode && (
                  <>
                    {project?.briefing && (
                      <button
                        className={`${styles.headerActionButton} ${styles.aiButton}`}
                        disabled={!!project?.is_closed}
                        onClick={() => {
                          if (project?.is_closed) { toast.info('Project is closed'); return; }
                          insightsPanelRef.current?.handleFSQAction();
                        }}
                        title="Generate and research focus questions from project briefing"
                      >
                        <AutoAwesomeIcon fontSize="small" />
                        Focus Questions
                      </button>
                    )}
                    <button
                      className={`${styles.headerActionButton} ${styles.aiButton}`}
                      disabled={!!project?.is_closed || insightsPanelLoading.isRunningResearch}
                      onClick={() => {
                        if (project?.is_closed) { toast.info('Project is closed'); return; }
                        insightsPanelRef.current?.handleResearchAction();
                      }}
                      title="Run desk research based on project resources"
                    >
                      <AutoAwesomeIcon fontSize="small" />
                      {insightsPanelLoading.isRunningResearch ? 'Starting...' : 'Desk Research'}
                    </button>
                  </>
                )}
                {/* Suggestion mode actions - replaces normal actions when selecting */}
                {activeStepIndex === 2 && insightsPanelLoading.isSuggestionMode && (
                  <>
                    <button
                      type="button"
                      className={`${styles.headerActionButton} ${styles.headerDeleteButton}`}
                      onClick={() => insightsPanelRef.current?.handleCancelSuggestionMode()}
                      disabled={insightsPanelLoading.isAddingResource}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`${styles.headerActionButton} ${styles.headerActionButtonPrimary}`}
                      onClick={() => insightsPanelRef.current?.handleAddSelectedSuggestions()}
                      disabled={insightsPanelLoading.selectedSuggestionCount === 0 || insightsPanelLoading.isAddingResource}
                    >
                      {insightsPanelLoading.isAddingResource ? 'Adding...' : `Add Selected (${insightsPanelLoading.selectedSuggestionCount})`}
                    </button>
                  </>
                )}
                {/* FSQ mode actions - replaces normal actions when in FSQ mode */}
                {activeStepIndex === 2 && insightsPanelLoading.isFSQMode && !insightsPanelLoading.isFSQGenerating && (
                  <>
                    <button
                      type="button"
                      className={`${styles.headerActionButton} ${styles.headerDeleteButton}`}
                      onClick={() => insightsPanelRef.current?.handleCancelFSQMode()}
                      disabled={insightsPanelLoading.isFSQRunning}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`${styles.headerActionButton} ${styles.headerActionButtonPrimary}`}
                      onClick={() => insightsPanelRef.current?.handleRunSelectedFSQ()}
                      disabled={insightsPanelLoading.selectedFSQCount === 0 || insightsPanelLoading.isFSQRunning}
                    >
                      {insightsPanelLoading.isFSQRunning ? 'Starting...' : `Run Research (${insightsPanelLoading.selectedFSQCount})`}
                    </button>
                  </>
                )}

                {/* Creative board actions */}
                {activeStepIndex >= 3 && currentFlowStep && (
                  <>
                    {/* AI action buttons */}
                    {creativeBoardState.applicableActions.map((action) => {
                      const needsResources = action.category === 'invent' && !(project?._resources && project._resources.length > 0);
                      return (
                      <button
                        key={action.name}
                        className={`${styles.headerActionButton} ${styles.aiButton}`}
                        disabled={!!project?.is_closed || needsResources}
                        onClick={() => {
                          if (project?.is_closed) { toast.info('Project is closed'); return; }
                          creativeBoardRef.current?.handleActionMenuItemClick(action);
                        }}
                        title={action.formatted_description}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                        {action.formatted_label}
                      </button>
                      );
                    })}
                    {/* Add new button */}
                    {(() => {
                      const typeName = flow?.creative_types?.find((ct: SparkCreativeType) => ct.fqn === currentFlowStep?.creative_type_fqn)?.name?.toLowerCase() || 'item';
                      return (
                        <button
                          className={styles.headerActionButton}
                          onClick={() => {
                            if (project?.is_closed) { toast.info('Project is closed'); return; }
                            handleCreateClick();
                          }}
                          disabled={!!project?.is_closed}
                          title={`Add new ${typeName}`}
                        >
                          <AddIcon fontSize="small" />
                          Add {typeName}
                        </button>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>

            {/* Suggestion mode alert bar */}
            {activeStepIndex === 2 && insightsPanelLoading.isSuggestionMode && (
              <ModeAlertBar
                message={
                  insightsPanelLoading.isSuggestingResources ? "Finding relevant dossiers..." :
                    insightsPanelLoading.isAddingResource ? "Adding selected dossiers..." :
                      "Select dossiers to add to your project"
                }
              />
            )}

            {/* FSQ mode alert bar */}
            {activeStepIndex === 2 && insightsPanelLoading.isFSQMode && (
              <ModeAlertBar
                message={
                  insightsPanelLoading.isFSQGenerating ? "Generating focus questions..." :
                    insightsPanelLoading.isFSQRunning ? "Starting research jobs..." :
                      "Review focus questions for research"
                }
              >
                {!insightsPanelLoading.isFSQGenerating && !insightsPanelLoading.isFSQRunning && (
                  <button
                    type="button"
                    className={`${styles.webSearchToggle} ${insightsPanelLoading.fsqWebSearchEnabled ? styles.webSearchToggleActive : ''}`}
                    onClick={() => insightsPanelRef.current?.handleToggleFSQWebSearch()}
                    title="Include web resources to supplement the research"
                  >
                    <PublicIcon fontSize="small" />
                    Web Search
                  </button>
                )}
              </ModeAlertBar>
            )}

            {/* Briefing assistant mode alert bar */}
            {activeStepIndex === 1 && isBriefingAssistantOpen && (
              <ModeAlertBar
                message={
                  briefingMode.isStarting ? "Starting assistant..." :
                    briefingMode.isThinking ? "DeepSights is thinking..." :
                      briefingMode.isEditing ? "Editing briefing - ESC to cancel" :
                        briefingMode.isChatting ? "Processing your request..." :
                          "Chat with the assistant, or click the briefing to edit"
                }
              />
            )}

            {/* Content body */}
            <div className={styles.contentBody}>
              {activeStepIndex === 0 ? (
                <ProjectSetupPanel
                  project={project}
                />
              ) : activeStepIndex === 1 ? (
                isBriefingAssistantOpen ? (
                  <BriefingAssistantOverlay
                    ref={briefingAssistantRef}
                    project={project}
                    flow={flow}
                    onBriefingUpdated={triggerRefreshData}
                    onClose={() => {
                      setIsBriefingAssistantOpen(false);
                      setUploadedBriefingContent(null);
                      setBriefingMode({ isEditing: false, isChatting: false, isStarting: false, isThinking: false });
                    }}
                    initialContent={uploadedBriefingContent}
                    onModeChange={setBriefingMode}
                  />
                ) : (
                  <ProjectBriefingPanel
                    project={project}
                    flow={flow}
                    onOpenAssistant={() => setIsBriefingAssistantOpen(true)}
                    onUpload={() => docxFileInputRef.current?.click()}
                  />
                )
              ) : activeStepIndex === 2 ? (
                <ProjectInsightsWallPanel
                  ref={insightsPanelRef}
                  project={project}
                  jobs={jobs || []}
                  isClosed={!!project.is_closed}
                  hasArticlesAvailable={hasArticlesAvailable}
                  onLoadingStateChange={setInsightsPanelLoading}
                  onNavigateToBriefing={() => handleStepSelect(1)}
                  understandingLabel={flow?.understanding_label}
                />
              ) : currentFlowStep ? (
                <CreativeBoard
                  ref={creativeBoardRef}
                  stepDefinition={currentFlowStep}
                  allCreatives={creatives || []}
                  flowDefinition={flow}
                  onCreateClick={handleCreateClick}
                  onDeleteCreatives={handleDeleteCreatives}
                  onTriggerAction={handleTriggerAction}
                  onCreativeClick={handleCreativeClick}
                  refreshData={triggerRefreshData}
                  dataRefreshCounter={dataRefreshCounter}
                  projectTagPattern={project.tag_pattern}
                  isProjectClosed={!!project.is_closed}
                  onStateChange={setCreativeBoardState}
                  onNavigateToStep={handleStepSelect}
                  onOpenCreativeEditor={handleOpenCreativeEditor}
                  hasBriefing={!!project.briefing}
                  hasResources={!!(project._resources && project._resources.length > 0)}
                  onNavigateToBriefing={() => handleStepSelect(1)}
                  onNavigateToResources={() => handleStepSelect(2)}
                />
              ) : (
                // Handle case where index > 2 but step is somehow undefined
                <div className={styles.error}>Invalid step selected.</div>
              )}
            </div>

            {/* Footer bar for navigating to next step */}
            {activeStepIndex < allSteps.length - 1 && !(activeStepIndex === 1 && isBriefingAssistantOpen) && (
              <div className={styles.contentFooter}>
                {completedSteps[activeStepIndex] ? (
                  <>
                    <span className={styles.contentFooterText}>
                      Continue to {allSteps[activeStepIndex + 1]?.description?.toLowerCase()}
                    </span>
                    <button
                      className={styles.contentFooterButton}
                      onClick={() => handleStepSelect(activeStepIndex + 1)}
                    >
                      Go to next step &gt;
                    </button>
                  </>
                ) : (
                  <span className={styles.contentFooterText}>
                    Complete this step to proceed to {allSteps[activeStepIndex + 1]?.description?.toLowerCase()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Editor now renders as a full view via early return when selectedCreativeFqn is set */}

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the selected item(s)?\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isConfirming={isDeleting}
        variant="destructive"
      />

      {/* Close project confirmation modal */}
      <ConfirmationModal
        isOpen={isConfirmCloseOpen}
        onClose={() => setIsConfirmCloseOpen(false)}
        onConfirm={handleConfirmClose}
        title="Close Project?"
        message="Closing the project makes all content read-only. You can re-open it later."
        confirmText="Close"
        isConfirming={isTogglingClosed}
        variant="destructive"
      />

      {/* Re-open project confirmation modal */}
      <ConfirmationModal
        isOpen={isConfirmReopenOpen}
        onClose={() => setIsConfirmReopenOpen(false)}
        onConfirm={handleConfirmReopen}
        title="Re-open Project?"
        message="Re-opening will allow edits and new content."
        confirmText="Re-open"
        isConfirming={isTogglingClosed}
        variant="primary"
      />

      {/* --- Spark Action Modal --- */}
      {currentAction && projectFqn && (
        <SparkActionModal
          isOpen={isActionModalOpen}
          onClose={handleCloseActionModal}
          title={currentAction.formatted_label}
          description={currentAction.formatted_description}
          actionName={currentAction.name}
          projectFqn={projectFqn}
          actionArgs={currentActionArgs}
          isGuidanceRequired={false}
          sourceTypeFqn={currentAction.input_type_fqn}
          targetTypeFqn={currentAction.output_type_fqn}
          resultKind={currentAction.result_kind}
          outputTypeName={currentAction.output_type_fqn
            ? flow.creative_types.find((creativeType) => creativeType.fqn === currentAction.output_type_fqn)?.plural_name
              || flow.creative_types.find((creativeType) => creativeType.fqn === currentAction.output_type_fqn)?.name
            : undefined}
          currentTypeName={currentAction.input_type_fqn
            ? flow.creative_types.find((creativeType) => creativeType.fqn === currentAction.input_type_fqn)?.name
            : selectedCreativeType?.name}
          isCurrentStep={
            currentAction.result_kind === 'creation' &&
            currentAction.output_type_fqn === (activeStepIndex >= 3 ? flow.steps[activeStepIndex - 3]?.creative_type_fqn : null)
          }
        />
      )}

      {/* Navigate away from briefing assistant confirmation */}
      <ConfirmationModal
        isOpen={isNavigateAwayModalOpen}
        onClose={handleCancelNavigateAway}
        onConfirm={handleConfirmNavigateAway}
        title="Discard Changes?"
        message="You have unsaved changes to the briefing. Are you sure you want to navigate away? All changes will be lost."
        confirmText="Discard"
        cancelText="Stay"
        variant="destructive"
      />

      {/* Hidden file input for DOCX upload */}
      <input
        type="file"
        ref={docxFileInputRef}
        onChange={handleDocxFileChange}
        style={{ display: 'none' }}
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      />

    </div>
  );
};
